import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail, CommonUtils } from '../../utils/commonUtils';
import { ProductFormUtils } from '../../utils/productCommon';
import { getBrandListFlat, type FlatBrandNode } from '../../utils/brand';
import {
  getProductOptionGroupDetail,
  getProductOptionGroupItems,
  updateProductOptionGroup,
  replaceProductOptionGroupItems,
  type ProductOptionGroupApiErrorItem,
} from '../../utils/productOptionGroup';
import ProductOptionGroupFormLayout, {
  type ProductOptionGroupTabId,
} from './components/ProductOptionGroupFormLayout';
import ProductOptionGroupInfoTab from './components/ProductOptionGroupInfoTab';
import ProductOptionGroupInfoSummary from './components/ProductOptionGroupInfoSummary';
import ProductOptionGroupProductsTab from './components/ProductOptionGroupProductsTab';
import {
  DEFAULT_OPTION_GROUP_FORM,
  type ProductOptionGroupFormData,
  type ProductOptionGroupFormErrors,
  type OptionGroupProductLine,
} from './components/productOptionGroupFormTypes';
import { formatDateTimeFromApi } from '../../utils/dateTime';
import { AlertCircle, History, RefreshCw } from 'lucide-react';

const VALID_TABS: ProductOptionGroupTabId[] = ['info', 'products'];

const ProductOptionGroupUpdate: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { groupCode: groupCodeParam } = useParams<{ groupCode: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab: ProductOptionGroupTabId =
    tabParam && (VALID_TABS as readonly string[]).includes(tabParam)
      ? (tabParam as ProductOptionGroupTabId)
      : 'info';

  const setActiveTab = (tab: ProductOptionGroupTabId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('product-option-group-list');

  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);

  const [formData, setFormData] = useState<ProductOptionGroupFormData>(() => {
    const st = location.state as { formData?: ProductOptionGroupFormData } | undefined;
    if (st?.formData) return st.formData;
    return {
      ...DEFAULT_OPTION_GROUP_FORM,
      code: decodeURIComponent(groupCodeParam || ''),
    };
  });
  const [errors, setErrors] = useState<ProductOptionGroupFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingProducts, setIsSavingProducts] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [productLines, setProductLines] = useState<OptionGroupProductLine[]>([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [originalData, setOriginalData] = useState<ProductOptionGroupFormData | null>(null);
  const [optionGroupMetadata, setOptionGroupMetadata] = useState<{
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
  } | null>(null);
  const isFetchingRef = React.useRef(false);

  ProductFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    const p = ProductFormUtils.loadProjectDetail();
    if (p) setProjectDetail(p);
  }, []);

  useEffect(() => {
    if (!projectDetail) return;
    const pc = projectDetail.code || getProjectCode();
    if (!pc) return;
    let cancelled = false;
    getBrandListFlat(pc)
      .then((res) => {
        if (!cancelled && res?.brands) setBrands(res.brands);
      })
      .catch((e) => {
        if (!cancelled) console.error(e);
      });
    return () => {
      cancelled = true;
    };
  }, [projectDetail?.code]);

  const projectCode = projectDetail?.code || getProjectCode() || '';

  useEffect(() => {
    const load = async () => {
      if (!projectCode || !groupCodeParam) return;
      if (isFetchingRef.current) return;
      try {
        isFetchingRef.current = true;
        setIsLoadingDetail(true);
        setSubmitError(null);
        const detail = await getProductOptionGroupDetail(projectCode, decodeURIComponent(groupCodeParam));
        const mappedItems = await getProductOptionGroupItems(projectCode, decodeURIComponent(groupCodeParam));
        const mapped: ProductOptionGroupFormData = {
          brandCode: detail.brandCode,
          code: detail.code,
          status: detail.status,
          name: detail.name,
          required: Number(detail.mandatory) === 1,
          multiSelect: Number(detail.multi) === 1,
          maxSelectOptions: Number(detail.maxSelect) || 0,
          minSelectOptions: Number(detail.minSelect) || 0,
          maxOptionQtySum: Number(detail.maxSumQuantity) || 0,
          minOptionQtySum: Number(detail.minSumQuantity) || 0,
        };
        setFormData(mapped);
        setOriginalData(mapped);
        const mappedLines = mappedItems.map((item, idx) => ({
          productCode: item.productCode,
          productName: item.productName || item.productCode,
          defaultSelected: Number(item.defaultSelect) === 1,
          defaultQty: Number(item.defaultQuantity) || 0,
          maxQty: Number(item.maxQuantity) || 0,
          minQty: Number(item.minQuantity) || 0,
          sortOrder: item.sortOrder || String(idx + 1),
        }));
        console.log('[옵션그룹 상품 매핑 변환]', {
          groupCode: decodeURIComponent(groupCodeParam),
          apiItemCount: mappedItems.length,
          mappedLineCount: mappedLines.length,
          mappedLines,
        });
        setProductLines(mappedLines);
        setErrors({});
        if (detail.createdAt && detail.updatedAt) {
          setOptionGroupMetadata({
            createdAt: detail.createdAt,
            createdBy: detail.createdBy ?? '—',
            updatedAt: detail.updatedAt,
            updatedBy: detail.updatedBy ?? '—',
          });
        } else {
          setOptionGroupMetadata(null);
        }
      } catch (err) {
        setSubmitError(CommonUtils.getNetworkErrorMessage(err));
      } finally {
        isFetchingRef.current = false;
        setIsLoadingDetail(false);
      }
    };
    load();
  }, [projectCode, groupCodeParam]);

  const validate = (): boolean => {
    const e: ProductOptionGroupFormErrors = {};
    if (!formData.brandCode?.trim()) e.brandCode = '브랜드를 선택해주세요.';
    if (!formData.code?.trim()) e.code = '그룹 코드를 입력해주세요.';
    if (!formData.name?.trim()) e.name = '그룹 명칭을 입력해주세요.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const mapApiErrorsToForm = (items: ProductOptionGroupApiErrorItem[]): ProductOptionGroupFormErrors => {
    const mapped: ProductOptionGroupFormErrors = {};
    const fieldMap: Record<string, keyof ProductOptionGroupFormErrors> = {
      brandCode: 'brandCode',
      code: 'code',
      status: 'status',
      name: 'name',
      mandatory: 'status',
      multi: 'status',
      maxSelect: 'maxSelectOptions',
      minSelect: 'minSelectOptions',
      maxSumQuantity: 'maxOptionQtySum',
      minSumQuantity: 'minOptionQtySum',
    };
    items.forEach((e) => {
      const key = fieldMap[e.target];
      if (key && e.message) mapped[key] = e.message;
    });
    return mapped;
  };

  const handleInfoSubmit = async () => {
    setSubmitError(null);
    if (!validate()) return;
    if (!projectCode || !groupCodeParam) return;
    setIsSubmitting(true);
    try {
      const result = await updateProductOptionGroup(projectCode, decodeURIComponent(groupCodeParam), {
        brandCode: formData.brandCode,
        status: formData.status,
        name: formData.name,
        mandatory: formData.required ? 1 : 0,
        multi: formData.multiSelect ? 1 : 0,
        maxSelect: formData.maxSelectOptions,
        minSelect: formData.minSelectOptions,
        maxSumQuantity: formData.maxOptionQtySum,
        minSumQuantity: formData.minOptionQtySum,
      });
      if (!result.success) {
        if (result.errors?.length) {
          setErrors((prev) => ({ ...prev, ...mapApiErrorsToForm(result.errors || []) }));
        }
        setSubmitError(result.message || '옵션 그룹 수정에 실패했습니다.');
        return;
      }
      setOriginalData({ ...formData });
      if (result.data) {
        const d = result.data;
        if (d.createdAt && d.updatedAt) {
          setOptionGroupMetadata({
            createdAt: d.createdAt,
            createdBy: d.createdBy ?? '—',
            updatedAt: d.updatedAt,
            updatedBy: d.updatedBy ?? '—',
          });
        }
      }
    } catch (err) {
      setSubmitError(CommonUtils.getNetworkErrorMessage(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubmitError(null);
    if (originalData) setFormData({ ...originalData });
    setErrors({});
  };

  const handleSaveProducts = async () => {
    if (!groupCodeParam) return;
    setSubmitError(null);
    setIsSavingProducts(true);
    try {
      const requestItems = productLines.map((line) => ({
        productCode: line.productCode,
        defaultSelect: line.defaultSelected ? 1 : 0,
        defaultQuantity: Number(line.defaultQty) || 0,
        maxQuantity: Number(line.maxQty) || 0,
        minQuantity: Number(line.minQty) || 0,
        sortOrder: line.sortOrder,
      }));
      const requestBody = { items: requestItems };
      const result = await replaceProductOptionGroupItems(
        projectCode,
        decodeURIComponent(groupCodeParam),
        requestBody
      );
      if (!result.success) {
        setSubmitError(result.message || '상품 매핑 저장에 실패했습니다.');
        return;
      }
      alert('상품 매핑이 저장되었습니다.');
    } catch (err) {
      setSubmitError(CommonUtils.getNetworkErrorMessage(err));
    } finally {
      setIsSavingProducts(false);
    }
  };

  if (!projectDetail || !groupCodeParam || isLoadingDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail}
      />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <Topbar
          title="옵션그룹 수정"
          subtitle={projectDetail.name}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo
          projectDetail={projectDetail}
        />
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            {submitError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                <p className="text-sm text-red-800">{submitError}</p>
              </div>
            )}
            <ProductOptionGroupFormLayout
              activeTab={activeTab}
              onTabChange={setActiveTab}
              groupCode={decodeURIComponent(groupCodeParam)}
              onBackClick={() => navigate('/products/option-groups')}
            >
              {activeTab === 'info' && (
                <>
                  {optionGroupMetadata && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                      <div className="flex items-start justify-between">
                        <div>
                          <h2 className="text-lg font-semibold text-gray-900 mb-2">
                            {formData.name} ({formData.code})
                          </h2>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                            <div className="flex items-center">
                              <History className="w-4 h-4 mr-2 flex-shrink-0" />
                              생성:{' '}
                              {formatDateTimeFromApi(optionGroupMetadata.createdAt) ||
                                optionGroupMetadata.createdAt}{' '}
                              ({optionGroupMetadata.createdBy})
                            </div>
                            <div className="flex items-center">
                              <RefreshCw className="w-4 h-4 mr-2 flex-shrink-0" />
                              수정:{' '}
                              {formatDateTimeFromApi(optionGroupMetadata.updatedAt) ||
                                optionGroupMetadata.updatedAt}{' '}
                              ({optionGroupMetadata.updatedBy})
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  <ProductOptionGroupInfoTab
                    mode="update"
                    formData={formData}
                    setFormData={setFormData}
                    errors={errors}
                    setErrors={setErrors}
                    brands={brands}
                    onSubmit={handleInfoSubmit}
                    onReset={handleReset}
                    onCancel={() => navigate('/products/option-groups')}
                    isSubmitting={isSubmitting}
                    submitButtonText="저장"
                    codeReadOnly
                  />
                </>
              )}
              {activeTab === 'products' && (
                <div className="space-y-6">
                  <ProductOptionGroupInfoSummary
                    formData={formData}
                    brands={brands}
                  />
                  <ProductOptionGroupProductsTab
                    projectCode={projectCode}
                    brandCode={formData.brandCode}
                    brandName={brands.find((b) => b.code === formData.brandCode)?.name}
                    lines={productLines}
                    onLinesChange={setProductLines}
                  />
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => navigate('/products/option-groups')}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      목록으로
                    </button>
                    <button
                      type="button"
                      onClick={handleSaveProducts}
                      disabled={isSavingProducts}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
                    >
                      {isSavingProducts ? '저장 중...' : '상품 매핑 저장'}
                    </button>
                  </div>
                </div>
              )}
            </ProductOptionGroupFormLayout>
          </div>
        </main>
      </div>
    </div>
  );
};

export default ProductOptionGroupUpdate;